import { TabContext, TabList } from "@mui/lab";
import {
  Box,
  Grid,
  IconButton,
  styled,
  Tab,
  TableCell,
  tableCellClasses,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CommonTable from "../../Common/CommonTable";
import DataNotFound from "../../Common/DataNotFound";
import CustomPagination from "../../Common/CommonPagination";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  create_posts,
  delete_post,
  get_posts,
  update_post,
  uploadImage,
} from "../../../Redux/Actions/action";
import ErrorHandler from "../../../Utils/handler";
import { toast } from "react-toastify";
import { showLoader } from "../../../Redux/Slices/loaderSlice";
import { useDispatch } from "react-redux";
import { BucketURL } from "../../../ApiSetup/Api";
import ButtonContained from "../../Common/ButtonContained";
import CommonTextField from "../../Common/CommonTextField";
import CustomDialog from "../../Common/CustomDialog";
import CommonFileUpload from "../../Common/CommonFileUpload";
import TextLabel from "../../Common/CommonTextLabel";
import Assets from "../../Common/Assets";
import EditIcon from "@mui/icons-material/Edit";

const headerList = [
  "No",
  "Member Image",
  "Member Name",
  "Member Designation",
  "Action",
];
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme?.palette?.bgLightWhite?.main,
    fontSize: 14,
    // fontWeight: "bold",
    textAlign: "center", // Horizontally align content,
    lineHeight: "1.2rem",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "0px 10px !important",
    textAlign: "center", // Horizontally align content
    borderBottom: "2px solid #d9d6f3",
  },
}));

const OfficeMember = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("0");
  const [data, setData] = useState({});
  const [memberData, setMemberData] = useState({});
  const [error, setError] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [filePreView, setFilePreView] = useState({ isOpen: false, link: "", type: "" });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const tabData = [
    { label: "Office Bearer", value: "0", color: theme.palette.primary.main },
    { label: "Margdarshak Member", value: "1", color: "rgba(245, 134, 52, 1)" },
    { label: "Karobari Member", value: "2", color: "rgba(11, 176, 108, 1)" },
  ];

  const _get_posts = async () => {
    dispatch(showLoader(true));
    let body = {
      type: "Office-Member",
      memberType:
        value == 0
          ? "Office-Bearer"
          : value == 1
            ? "Margdarshak-Member"
            : "Karobari-Member",
      page: page,
      limit: rowsPerPage,
    };
    try {
      const result = await dispatch(get_posts(body));
      const error = ErrorHandler(result);
      if (error) {
        setMemberData(result?.payload?.data);
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _delete_post = async (id) => {
    dispatch(showLoader(true));
    try {
      const result = await dispatch(delete_post({ _id: id }));
      const error = ErrorHandler(result);
      if (error) {
        toast.success(result?.payload?.message);
        _get_posts();
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const handleClear = () => {
    setOpen(false);
    setIsEdit(false);
    setData({});
    setError({});
  };

  // --------------- handleChange ---------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value.trimStart() });
  };

  const validation = () => {
    let isValid = true;
    let error = {};
    if (!data.memberName) {
      error.memberName = "Member Name is required";
      isValid = false;
    }
    if (!data.memberImage) {
      error.memberImage = "Member Image is required";
      isValid = false;
    }
    if (!data.memberDesignation) {
      error.memberDesignation = "Member Designation is required";
      isValid = false;
    }
    setError(error);
    return isValid;
  };

  const handleImageUpload = async (e) => {
    const { files, name } = e?.target;

    const validImageTypes = ["image/jpeg", "image/png", "image/jpg"];
    const validTypes = [...validImageTypes];

    const fileData = files[0];
    if (!validTypes.includes(fileData.type)) {
      setError((error) => ({
        ...error,
        memberImageSize:
          "Invalid file type. Only JPEG, PNG and JPG are allowed.",
      }));
      return;
    }

    if (fileData.size > 10485760) {
      setError((error) => ({
        ...error,
        memberImageSize:
          "File size exceeds the 10 MB limit. Please upload a smaller file.",
      }));
      return;
    }

    const formData = new FormData();
    formData.append("image", files[0]);

    dispatch(showLoader(true));
    try {
      const result = await dispatch(uploadImage(formData));
      const error = ErrorHandler(result);

      if (error) {
        const uploadedFile = result?.payload?.data?.[0]?.image;

        setData((prevState) => ({
          ...prevState,
          [name]: uploadedFile,
          isImage: validImageTypes.includes(fileData.type),
        }));

        setError((error) => ({
          ...error,
          memberImageSize: "",
          bannerImage: "",
        }));
      }
    } catch (err) {
      console.error("File upload failed:", err);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const _createUpdatePosts = async () => {
    if (validation()) {
      dispatch(showLoader(true));
      try {
        let body = {
          type: "Office-Member",
          officeMember: {
            memberImage: data?.memberImage,
            memberDesignation: data?.memberDesignation,
            memberName: data?.memberName,
            memberType:
              value == 0
                ? "Office-Bearer"
                : value == 1
                  ? "Margdarshak-Member"
                  : "Karobari-Member",
          },
        };
        if (isEdit && data?._id) {
          body._id = data?._id;
        }
        const fn = isEdit ? update_post : create_posts;
        const result = await dispatch(fn(body));
        const error = ErrorHandler(result);
        if (error) {
          toast.success(result?.payload?.message);
          _get_posts();
          handleClear();
        }
      } catch (error) {
        toast.error(error?.message || "An unknown error occurred");
      } finally {
        dispatch(showLoader(false));
      }
    }
  };

  useEffect(() => {
    _get_posts();
  }, [value, page, rowsPerPage]);

  return (
    <div>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "20px", fontWeight: 500, mb: 2 }}>
          Office Member
        </Typography>
        <ButtonContained text={"Add Member"} onClick={() => setOpen(true)} />
      </Box>
      <TabContext value={value}>
        <Box mb={1}>
          <TabList
            onChange={(e, newValue) => {
              if (newValue !== undefined) {
                setValue(newValue);
                setPage(1);
              }
            }}
            aria-label="tabs with icons"
            sx={{
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .Mui-selected": {
                background: tabData?.filter((e) => e?.value == value)?.[0]
                  ?.color,
                color: `${theme.palette.common.white} !important`,
              },
            }}
          >
            {tabData?.map((tab) => {
              return (
                <Tab
                  label={`${tab?.label}`}
                  value={tab?.value}
                  sx={{
                    marginRight: "10px",
                    border: `2px solid ${tab.color}`,
                    borderRadius: "10px",
                    color: tab.color,
                    height: "40px",
                    minHeight: "0px",
                  }}
                />
              );
            })}
          </TabList>
        </Box>
        <Grid item xs={12}>
          <CommonTable headerList={headerList}>
            {memberData?.data?.length > 0 ? (
              memberData?.data?.map((item, index) => (
                <TableRow key={item.id}>
                  <StyledTableCell align="center">
                    {(index % rowsPerPage) + 1 + (page - 1) * rowsPerPage}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {
                      <img
                        src={BucketURL + item?.officeMember?.memberImage}
                        height={"40px"}
                        width={"40px"}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          const url = BucketURL + item?.officeMember?.memberImage;
                          setFilePreView({ isOpen: true, link: url, type: "image" });
                        }}
                      />
                    }
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {item?.officeMember?.memberName}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    {item?.officeMember?.memberDesignation}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                      justifyContent={"center"}
                    >
                      <IconButton sx={{ padding: 0 }} onClick={() => {
                        setOpen(true); setIsEdit(true); setData({
                          _id: item?._id,
                          memberName: item.officeMember?.memberName,
                          memberDesignation: item?.officeMember?.memberDesignation,
                          memberImage: item?.officeMember?.memberImage,
                        })
                      }} >
                        <EditIcon fontSize="small" color='success' />
                      </IconButton>
                      <IconButton
                        sx={{ padding: 0 }}
                        onClick={() => {
                          _delete_post(item?._id);
                        }}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </StyledTableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <StyledTableCell colSpan={8}>
                  <DataNotFound
                    title={"No Records Found"}
                    icon={
                      <ErrorOutlineIcon
                        color="primary"
                        style={{ fontSize: "3rem" }}
                      />
                    }
                    elevation={0}
                  />
                </StyledTableCell>
              </TableRow>
            )}
          </CommonTable>
          {memberData?.count > 0 && (
            <Grid item xs={12}>
              <CustomPagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={Math.ceil(memberData?.count / rowsPerPage)}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          )}
        </Grid>
      </TabContext>
      {open && (
        <CustomDialog
          isOpen={open}
          handleClose={handleClear}
          title={isEdit ? "Update Member" : "Add Member"}
          padding={"10px"}
          width={500}
          action={
            <>
              <ButtonContained
                text={isEdit ? "Update Member" : "Add Member"}
                onClick={_createUpdatePosts}
                minWidth={"100%"}
                marginTop={"10px"}
              />
            </>
          }
        >
          <Grid container spacing={2} xs={12} ml={-1}>
            <Grid item xs={12}>
              <CommonTextField
                value={data?.memberName}
                width={"100%"}
                text="Member Name"
                type="text"
                onChange={(e) => handleInputChange(e)}
                placeholder="Enter a Member Name"
                name="memberName"
                valid
              />
              <TextLabel
                title={!data?.memberName ? error?.memberName : ""}
                color="error"
              />
            </Grid>
            <Grid item xs={12}>
              <CommonTextField
                value={data?.memberDesignation}
                width={"100%"}
                text="Member Designation"
                type="text"
                onChange={(e) => handleInputChange(e)}
                placeholder="Enter a Member Designation"
                name="memberDesignation"
                valid
              />
              <TextLabel
                title={!data?.memberDesignation ? error?.memberDesignation : ""}
                color="error"
              />
            </Grid>
            <Grid item xs={12}>
              <CommonFileUpload
                text={"Upload a Member Image"}
                padding={{ xs: "5px 14px", sm: "30px 14px", lg: "35px 7px" }}
                accept="image/jpeg, image/png, image/jpg"
                labelTitle="image/jpeg, image/png, image/jpg Up to 10MB"
                name={"memberImage"} // This is the key that will be logged in handleUpload
                onChange={(e) => handleImageUpload(e)}
                valid
              />
              {data?.memberImage && (
                <Assets
                  src={BucketURL + data?.memberImage}
                  height={"50px"}
                  width={"50px"}
                  alt="Banner Image"
                />
              )}
              <TextLabel
                title={!data?.memberImage ? error?.memberImage : ""}
                color="error"
              />
              <TextLabel
                title={data?.memberImageSize ? "" : error?.memberImageSize}
                color="error"
              />
            </Grid>
          </Grid>
        </CustomDialog>
      )}
      {filePreView?.isOpen &&
        <CustomDialog
          isOpen={filePreView?.isOpen} handleClose={() => setFilePreView({ isOpen: false, link: "", type: "" })} title={"View Image"} padding={'5px'}
          width={400}
          action={
            filePreView?.type === "image" ? (<img src={filePreView?.link} alt="Preview" style={{ width: '100%' }} />
            ) : filePreView?.type === "video" ?
              (<video controls style={{ width: '100%' }}>
                <source src={filePreView?.link} type="video/mp4" />
                Your browser does not support the video tag.
              </video>)
              : (<div>No media available</div>)
          }
          maxWidth={'md'}
        >
        </CustomDialog>}
    </div>
  );
};

export default OfficeMember;
